import { useCallback, useState } from "react";
import { Jsonifiable } from "type-fest";

type LocalStorage = {
  "isx.fileBrowser.paginationSize": number;
  "isx.flyoutFiles.isExpanded": boolean;
  "isx.flyoutBottom.size": number;
  "isx.flyoutRight.size": number;
  "isx.ToolEditorConfigCollapsed": boolean;
};

const parseItem = <T extends keyof LocalStorage>(
  storageKey: T,
  defaultValue: LocalStorage[T],
) => {
  const item = localStorage.getItem(storageKey);
  if (item === null || item === "undefined") {
    const stringifiedDefault = JSON.stringify(defaultValue);
    localStorage.setItem(storageKey, stringifiedDefault);
    return defaultValue;
  }
  const parsedValue = JSON.parse(
    item,
    // ensures a only non-nullable jsonifiable types can be put in the LocalStorage type
  ) as LocalStorage[T] satisfies NonNullable<Jsonifiable>;
  return parsedValue;
};

export const useLocalStorage = <T extends keyof LocalStorage>(
  key: T,
  defaultValue: LocalStorage[T],
) => {
  const [value, setValue] = useState(parseItem<T>(key, defaultValue));

  const updateValue: React.Dispatch<React.SetStateAction<LocalStorage[T]>> =
    useCallback(
      (valueOrCallback) => {
        if (typeof valueOrCallback === "function") {
          setValue((prev) => {
            const setStateResult = valueOrCallback(prev);
            const stringifiedValue = JSON.stringify(setStateResult);

            localStorage.setItem(key, stringifiedValue);

            return setStateResult;
          });
        } else {
          const stringifiedValue = JSON.stringify(valueOrCallback);
          localStorage.setItem(key, stringifiedValue);

          setValue(valueOrCallback);
        }
      },
      [key],
    );

  return [value, updateValue] as const;
};
